import { createContext, useContext, useMemo, useState } from 'react';
import { Event } from 'src/modules/event/models/event';
import { EventTypeEnum } from '../models/enums/eventType.enum';

export interface EventContextSettings {
  event: Event | undefined;
  setEvent: (args: Event | undefined) => void;
  uploadedImageUrl: string;
  setUploadedImageUrl: (url: string) => void;
  uploadedImageFile: File | undefined;
  setUploadedImageFile: (arg: File | undefined) => void;
  croppedImage: File | undefined;
  setCroppedImage: (arg: File | undefined) => void;
  selectedEventType: EventTypeEnum | undefined;
  setSelectedEventType: (arg: EventTypeEnum | undefined) => void;
  editMode: boolean;
  setEditMode: (state: boolean) => void;
}

export const EventContext = createContext<EventContextSettings>({
  event: undefined,
  setEvent: () => undefined,
  uploadedImageUrl: '',
  setUploadedImageUrl: () => undefined,
  uploadedImageFile: undefined,
  setUploadedImageFile: () => undefined,
  croppedImage: undefined,
  setCroppedImage: () => undefined,
  selectedEventType: undefined,
  setSelectedEventType: () => undefined,
  editMode: false,
  setEditMode: () => undefined,
});

export const useEventContext = () => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useMemberContext must be within AppContext');
  }
  return context;
};

interface Props {
  children: JSX.Element;
}
export const EventContextProvider = ({ children }: Props) => {
  const [event, setEvent] = useState<Event | undefined>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
  const [uploadedImageFile, setUploadedImageFile] = useState<File | undefined>(undefined);
  const [croppedImage, setCroppedImage] = useState<File | undefined>(undefined);
  const [selectedEventType, setSelectedEventType] = useState<EventTypeEnum | undefined>(undefined);
  const [editMode, setEditMode] = useState<boolean>(false);

  const eventContext = useMemo(
    () => ({
      event,
      setEvent,
      uploadedImageUrl,
      setUploadedImageUrl,
      uploadedImageFile,
      setUploadedImageFile,
      croppedImage,
      setCroppedImage,
      selectedEventType,
      setSelectedEventType,
      editMode,
      setEditMode,
    }),
    [event, selectedEventType, uploadedImageFile, croppedImage, uploadedImageUrl, editMode],
  );

  return <EventContext.Provider value={eventContext}>{children}</EventContext.Provider>;
};
